import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
// @ts-expect-error import path to avoid circular dependency issues
import store from '@/store'
import { resolveTourVariant, type TourVariant } from '@/components/tour/console-tour-steps'

/**
 * Whether this person still needs the console orientation tour, and the record of what they did
 * with it last time.
 *
 * The record lives in server-side user preferences so it follows someone to a new browser or a
 * second computer -- being re-offered a tour you already declined, every time you sign in
 * somewhere new, is exactly the kind of nagging that makes people distrust in-app guidance. A
 * localStorage mirror backs it up so the very first render can decide without waiting for the
 * session request, and so the feature still behaves sanely when preferences fail to save.
 */

/**
 * Bump when the console changes enough that everyone should be re-offered the tour.
 *
 * 2: the customizable sidebar.
 */
export const TOUR_VERSION = 2

const STORAGE_KEY = 'CONSOLE_TOUR_STATE'

/**
 * How many times we auto-offer the tour to someone who neither accepts nor declines it -- who
 * closes the tab, or navigates away from the invitation. They asked for neither, so we try
 * again, but not forever.
 */
const MAX_AUTO_PROMPTS = 3

/** And not twice in one day. */
const PROMPT_THROTTLE_MS = 24 * 60 * 60 * 1000

/** Below this width the sidebar is a drawer and the header collapses, so the tour has no anchors. */
export const TOUR_MIN_VIEWPORT_WIDTH = 1024

export type TourStatus =
  /** Never offered, or offered and left unanswered. */
  | 'unseen'
  /** Reached the end. */
  | 'completed'
  /** Said no to the invitation, or skipped out partway. Do not offer again. */
  | 'dismissed'

export interface TourRecord {
  version: number
  status: TourStatus
  promptCount: number
  lastPromptedAt: string | null
  /** Where they bailed out, for working out which step loses people. */
  lastStepIndex: number | null
  updatedAt: string
}

function emptyRecord (): TourRecord {
  return {
    version: TOUR_VERSION,
    status: 'unseen',
    promptCount: 0,
    lastPromptedAt: null,
    lastStepIndex: null,
    updatedAt: new Date().toISOString()
  }
}

function isTourRecord (value: unknown): value is TourRecord {
  return !!value && typeof value === 'object' && typeof (value as TourRecord).status === 'string'
}

/**
 * Normalizes a stored record. A record written by an older tour version is treated as unseen so
 * the new tour gets offered, rather than being suppressed by a decision about different content.
 */
function normalizeRecord (value: unknown): TourRecord {
  if (!isTourRecord(value) || value.version !== TOUR_VERSION) {
    return emptyRecord()
  }

  return {
    version: TOUR_VERSION,
    status: value.status,
    promptCount: Number.isFinite(value.promptCount) ? value.promptCount : 0,
    lastPromptedAt: value.lastPromptedAt ?? null,
    lastStepIndex: Number.isFinite(value.lastStepIndex as number) ? value.lastStepIndex : null,
    updatedAt: value.updatedAt ?? new Date().toISOString()
  }
}

function readMirror (): TourRecord | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? normalizeRecord(JSON.parse(raw)) : null
  } catch {
    // Private browsing, blocked storage, or corrupt JSON. Server preferences still apply.
    return null
  }
}

function writeMirror (record: TourRecord) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // Worst case the next fresh session waits for the session request before deciding.
  }
}

export const useConsoleTourStore = defineStore('consoleTour', () => {
  const record = ref<TourRecord>(readMirror() ?? emptyRecord())

  /** True once the session request has landed, i.e. the server's opinion is in. */
  const serverRecordLoaded = ref(false)

  /** True while the tour is actually driving, so the shell can keep the sidebar open. */
  const isRunning = ref(false)

  let repushed = false

  const variant = computed<TourVariant>(() => resolveTourVariant(store.state.userCreated))

  /**
   * The server record wins over the mirror when they disagree: someone who completed the tour on
   * their work laptop should not be re-offered it at home just because that browser has no
   * mirror. `unseen` is the one status that never overrides a local decision, since it is also
   * what an absent record looks like.
   */
  function adoptServerRecord (stored: unknown) {
    const serverRecord = normalizeRecord(stored)
    serverRecordLoaded.value = true

    if (serverRecord.status === 'unseen' && record.value.status !== 'unseen') {
      // Local knows something the server does not -- most likely a save that failed. Re-push it,
      // once: a failed save rolls the preferences back, which lands right here again, and
      // re-pushing every time would retry forever while offline.
      if (!repushed) {
        repushed = true
        void persist(record.value)
      }
      return
    }

    record.value = serverRecord
    writeMirror(serverRecord)
  }

  watch(
    () => store.state.userPreferences?.consoleTour,
    stored => {
      if (stored !== undefined) {
        adoptServerRecord(stored)
      }
    },
    { immediate: true }
  )

  // An org with no stored tour preference still needs `serverRecordLoaded` to flip, or the tour
  // would never be offered to anyone who has not already seen it.
  watch(
    () => store.state.settingsLoaded,
    loaded => {
      if (loaded && store.state.userPreferences?.consoleTour === undefined) {
        serverRecordLoaded.value = true
      }
    },
    { immediate: true }
  )

  async function persist (next: TourRecord) {
    record.value = next
    writeMirror(next)

    try {
      await store.dispatch('patchUserPreferences', { consoleTour: next })
    } catch {
      // The mirror already holds it, so this session behaves correctly either way. Nothing here
      // is worth interrupting someone over.
    }
  }

  function update (patch: Partial<TourRecord>) {
    return persist({
      ...record.value,
      ...patch,
      version: TOUR_VERSION,
      updatedAt: new Date().toISOString()
    })
  }

  /**
   * Whether this person has an outstanding answer to give. False once they complete or decline,
   * and false once we have asked enough times without getting one.
   */
  const hasOutstandingOffer = computed(() => {
    if (record.value.status !== 'unseen') {
      return false
    }
    if (record.value.promptCount >= MAX_AUTO_PROMPTS) {
      return false
    }
    if (!record.value.lastPromptedAt) {
      return true
    }

    const last = new Date(record.value.lastPromptedAt).getTime()
    return !Number.isFinite(last) || Date.now() - last >= PROMPT_THROTTLE_MS
  })

  /**
   * Everything that has to be true before we interrupt someone with an invitation. Deliberately
   * conservative: a tour shown to a half-loaded console, to a clock-only user with no navigation,
   * or over a fatal error screen is worse than no tour at all.
   */
  const canAutoOffer = computed(() => {
    const state = store.state
    const getters = store.getters

    return (
      serverRecordLoaded.value &&
      hasOutstandingOffer.value &&
      state.settingsLoaded &&
      state.organizationActive &&
      !state.fatalError &&
      !getters.needsSetup &&
      !getters.noConsoleAccess &&
      getters.insideOrgNamespace &&
      !getters.inSignUpWizard
    )
  })

  function markOffered () {
    logEvent('console_tour_offered', { variant: variant.value })
    return update({
      promptCount: record.value.promptCount + 1,
      lastPromptedAt: new Date().toISOString()
    })
  }

  function markDeclined () {
    logEvent('console_tour_declined', { variant: variant.value })
    return update({ status: 'dismissed' })
  }

  function markCompleted () {
    logEvent('console_tour_completed', { variant: variant.value })
    return update({ status: 'completed', lastStepIndex: null })
  }

  function markSkipped (stepIndex: number, stepId: string) {
    logEvent('console_tour_skipped', { variant: variant.value, step_index: stepIndex, step_id: stepId })
    return update({ status: 'dismissed', lastStepIndex: stepIndex })
  }

  function markStepViewed (stepIndex: number, stepId: string) {
    logEvent('console_tour_step_viewed', { variant: variant.value, step_index: stepIndex, step_id: stepId })
  }

  function markStarted (source: 'auto' | 'help_menu') {
    isRunning.value = true
    logEvent('console_tour_started', { variant: variant.value, source })
  }

  function markStopped () {
    isRunning.value = false
  }

  /** Test seam: drop everything and start over. */
  function reset () {
    return persist(emptyRecord())
  }

  return {
    record,
    isRunning,
    variant,
    serverRecordLoaded,
    canAutoOffer,
    hasOutstandingOffer,
    markOffered,
    markDeclined,
    markCompleted,
    markSkipped,
    markStepViewed,
    markStarted,
    markStopped,
    reset
  }
})
